import React, { useEffect, useState } from 'react';

// lodash
import _ from 'lodash';

// components
import { useTheme, Skeleton, Grid } from '@mui/material';

// utils
import { makeStyles } from '@mui/styles';
import cards from './quick-actions';
import { useGetQuoteSubjectivityStatus } from '../../../components/hooks/useGetQuoteSubjectivities';
import { useAllow } from '../../../components/hooks/useAllow';
import { QuickActionCardContext } from './quick-actions/context/QuickActionCardContext';
import { StyledHeader, StyledParagraph } from './quick-actions/styles';
import {
  getLatestPolicies,
  getLatestQuotes,
} from '../_services/dashboard.service';
import { readCoverageList } from './coverage/useCoverageView';
import { selectQuickActionSubjects } from './coverage/quickActionSubjects';

// ui
export const QuickActionCard = () => {
  const { palette } = useTheme();
  const classes = useGeneralStyles();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('Support');
  const [_quote, setQuote] = useState({});
  const [_policy, setPolicy] = useState({});

  const {
    query: { isInitialLoading: isLoadingSubjectivities },
    canQuoteBeApproved,
    canUserAttest,
  } = useGetQuoteSubjectivityStatus({
    // @ts-expect-error TS(2339): Property 'id' does not exist on type '{}'.
    quoteId: _quote.id,
    // @ts-expect-error TS(2339): Property 'product' does not exist on type '{}'.
    productType: _quote.product,
  });

  const CAN_MANAGE_QUOTE = useAllow(['quote:manage']);
  const CAN_PAY_QUOTE = useAllow(['quote:pay']);
  const CAN_MANAGE_CLAIMS = useAllow(['claims:manage']);

  useEffect(() => {
    Promise.all(
      [getLatestPolicies(), getLatestQuotes()].map((p) =>
        p.catch(() => undefined)
      )
    )
      .then(([policyData, quotesData]) => {
        // Both reads return arrays as of fir-account#6228. Reading `.status` and
        // `.customerStatus` straight off the response used to work when it was a single
        // object; against an array every branch below misses and the rail silently falls
        // through to the generic Support card.
        const { policy, quote } = selectQuickActionSubjects(
          readCoverageList(_.get(policyData, 'data')),
          readCoverageList(_.get(quotesData, 'data'))
        );

        setQuote(quote);
        setPolicy(policy);

        const agencyBilled = _.get(
          policy,
          'firmographicData.agencyBilled',
          false
        );

        if (quote.customerStatus === 'AVAILABLE' && CAN_MANAGE_QUOTE) {
          setStatus(
            quote.isRenewal || quote.isMigration ? 'RenewReady' : 'ApproveQuote'
          );
          return setLoading(false);
        }

        if (policy.status === 'BOUND' && !policy.isAppSigned && CAN_PAY_QUOTE) {
          if (!policy.quoteId || !policy.product) {
            return setLoading(false);
          }
          setStatus(
            policy.isRenewal || policy.isMigration
              ? 'RenewBound'
              : 'AttestQuote'
          );
          return setLoading(false);
        }

        if (
          (['ACTIVE', 'ISSUED', 'IN FORCE'].includes(policy.status) ||
            (policy.status === 'BOUND' && policy.isAppSigned)) &&
          policy &&
          policy.totalPremium > 0 &&
          !policy.isPaid &&
          !agencyBilled &&
          CAN_PAY_QUOTE
        ) {
          setStatus('PayPolicy');
          return setLoading(false);
        }

        if (
          policy.status === 'IN FORCE' &&
          policy &&
          (policy.isPaid || agencyBilled) &&
          CAN_MANAGE_CLAIMS
        ) {
          setStatus('FileClaim');
          return setLoading(false);
        }

        setLoading(false);
      })
      .catch(() => {
        setQuote({});
        setPolicy({});
        setLoading(false);
      });
  }, [CAN_MANAGE_QUOTE, CAN_PAY_QUOTE, CAN_MANAGE_CLAIMS]);

  if (loading || !cards[status] || isLoadingSubjectivities) {
    return (
      <Grid container columnSpacing={2} rowSpacing={2}>
        <Grid
          item
          md={3}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Skeleton component="img" variant="rounded" height={64} width={64} />
        </Grid>
        <Grid item md={9}>
          <StyledHeader>
            <Skeleton variant="text" width="50%" />
          </StyledHeader>
          <StyledParagraph>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="70%" />
          </StyledParagraph>
          <Skeleton
            variant="rounded"
            width="100%"
            height={40}
            sx={{ marginTop: '1rem' }}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <QuickActionCardContext.Provider
      value={{
        agent: null,
      }}
    >
      {React.createElement(cards[status], {
        cl: classes,

        iconFill: palette.common.cowbellBlue,
        quote: _quote,
        policy: _policy,
        canQuoteBeApproved,
        canUserAttest,
      })}
    </QuickActionCardContext.Provider>
  );
};

const useGeneralStyles = makeStyles(() => ({
  icon: {
    display: 'block',
    width: '30%',
    margin: '20px auto',
  },
}));
